import React, { useState, useEffect } from 'react';

interface ProblemFormProps {
  initialData?: any;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

export const ProblemForm: React.FC<ProblemFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting
}) => {
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    difficulty: 'easy' as 'easy' | 'medium' | 'hard',
    description: '',
    functionName: '',
    starterCode: '',
    allowedSubmissions: -1,
    examples: [] as any[],
    publicTests: [] as any[],
    hiddenTests: [] as any[],
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id || '',
        title: initialData.title || '',
        difficulty: initialData.difficulty || 'easy',
        description: initialData.description || '',
        functionName: initialData.functionName || '',
        starterCode: initialData.starterCode || '',
        allowedSubmissions: initialData.allowedSubmissions ?? -1,
        examples: initialData.examples || [],
        publicTests: initialData.publicTests || [],
        hiddenTests: initialData.hiddenTests || [],
      });
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addTest = (type: 'examples' | 'publicTests' | 'hiddenTests') => {
    const newTest = {
      name: `Test ${formData[type].length + 1}`,
      args: [],
      expected: '',
      ...(type === 'examples' ? { explanation: '' } : {})
    };
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], newTest]
    }));
  };

  const removeTest = (type: 'examples' | 'publicTests' | 'hiddenTests', index: number) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_: any, i: number) => i !== index)
    }));
  };

  const updateTest = (type: 'examples' | 'publicTests' | 'hiddenTests', index: number, field: string, value: any) => {
    setFormData(prev => {
      const newTests = [...prev[type]];
      newTests[index] = { ...newTests[index], [field]: value };
      return { ...prev, [type]: newTests };
    });
  };

  const handleJsonChange = (type: 'examples' | 'publicTests' | 'hiddenTests', index: number, field: 'args' | 'expected', value: string) => {
    try {
      const parsed = JSON.parse(value);
      updateTest(type, index, field, parsed);
    } catch (e) {
      // Keep as string if not valid JSON yet (user typing)
      updateTest(type, index, field, value);
    }
  };

  const generateBoilerplate = () => {
    const fnName = formData.functionName || 'solution';
    // Try to find the number of arguments from existing tests
    let argCount = 0;
    const allTests = [...formData.examples, ...formData.publicTests, ...formData.hiddenTests];
    if (allTests.length > 0 && Array.isArray(allTests[0].args)) {
      argCount = allTests[0].args.length;
    }

    const args = Array.from({ length: argCount }, (_, i) => `arg${i + 1}`).join(', ');
    const boilerplate = `def ${fnName}(${args}):\n    # Write your solution here\n    pass`;
    
    setFormData(prev => ({ ...prev, starterCode: boilerplate }));
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setFormData(prev => ({ ...prev, starterCode: content }));
    };
    reader.readAsText(file);
    // Reset input
    e.target.value = '';
  };

  return (
    <form onSubmit={handleSubmit} className="bg-card p-8 rounded-xl border border-border flex flex-col gap-8 shadow-sm">
      <div className="flex justify-between items-center border-b border-border pb-6">
        <div>
          <h3 className="text-xl font-bold">{initialData ? 'Edit Problem' : 'Create New Problem'}</h3>
          <p className="text-sm text-muted-foreground mt-1">Define the problem statement, constraints, and test cases.</p>
        </div>
        <div className="flex gap-3">
          <button 
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-semibold bg-transparent border border-border rounded-lg hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button 
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : initialData ? 'Update Problem' : 'Create Problem'}
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Problem Title <span className="text-destructive">*</span>
          </label>
          <input 
            required
            type="text" 
            value={formData.title}
            onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g., Two Sum"
            className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Problem ID (Slug) <span className="text-destructive">*</span>
          </label>
          <input 
            required
            disabled={!!initialData}
            type="text" 
            value={formData.id}
            onChange={e => setFormData(prev => ({ ...prev, id: e.target.value }))}
            placeholder="e.g., two-sum"
            className={`p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all ${initialData ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Difficulty</label>
          <select 
            value={formData.difficulty}
            onChange={e => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
            className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          >
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Allowed Submissions</label>
          <div className="flex items-center gap-3">
            <input 
              type="number" 
              value={formData.allowedSubmissions}
              onChange={e => setFormData(prev => ({ ...prev, allowedSubmissions: parseInt(e.target.value) }))}
              className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all flex-1"
            />
            <span className="text-xs text-muted-foreground whitespace-nowrap">(-1 for unlimited)</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Description (Markdown) <span className="text-destructive">*</span>
        </label>
        <textarea 
          required
          rows={6}
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe the problem, input format, output format, and constraints..."
          className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-vertical min-h-[150px] font-sans"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Function Name <span className="text-destructive">*</span>
          </label>
          <input 
            required
            type="text" 
            value={formData.functionName}
            onChange={e => setFormData(prev => ({ ...prev, functionName: e.target.value }))}
            placeholder="e.g., solution"
            className="p-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Starter Code (Python) <span className="text-destructive">*</span>
          </label>
          <div className="flex gap-2">
            <button 
              type="button"
              onClick={generateBoilerplate}
              className="text-[10px] font-bold bg-secondary text-secondary-foreground border border-border px-2 py-1 rounded hover:bg-secondary/80 transition-colors flex items-center gap-1.5"
              title="Generate a Python function boilerplate based on function name and test arguments"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
              Generate
            </button>
            <label 
              className="text-[10px] font-bold bg-secondary text-secondary-foreground border border-border px-2 py-1 rounded hover:bg-secondary/80 transition-colors cursor-pointer flex items-center gap-1.5"
              title="Import starter code from a local file"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
              Import
              <input type="file" className="hidden" accept=".py,.txt" onChange={handleImportFile} />
            </label>
          </div>
        </div>
        <textarea 
          required
          rows={8}
          value={formData.starterCode}
          onChange={e => setFormData(prev => ({ ...prev, starterCode: e.target.value }))}
          placeholder="def solution(nums, target):&#10;    # Your code here&#10;    pass"
          className="p-4 rounded-lg border border-border bg-muted/50 focus:ring-2 focus:ring-primary/20 outline-none transition-all font-mono text-sm"
        />
      </div>

      <div className="space-y-12 mt-4">
        <TestSection 
          title="Examples"
          subtitle="Visible in problem description to help users understand the task."
          type="examples"
          tests={formData.examples}
          onAdd={() => addTest('examples')}
          onRemove={(i) => removeTest('examples', i)}
          onUpdate={(i, f, v) => updateTest('examples', i, f, v)}
          onJsonChange={(i, f, v) => handleJsonChange('examples', i, f, v)}
        />

        <TestSection 
          title="Public Tests"
          subtitle="Executed when the user clicks 'Run'. Results are visible to the user."
          type="publicTests"
          tests={formData.publicTests}
          onAdd={() => addTest('publicTests')}
          onRemove={(i) => removeTest('publicTests', i)}
          onUpdate={(i, f, v) => updateTest('publicTests', i, f, v)}
          onJsonChange={(i, f, v) => handleJsonChange('publicTests', i, f, v)}
        />

        <TestSection 
          title="Hidden Tests"
          subtitle="Used for authoritative grading. Results are only revealed after submission."
          type="hiddenTests"
          tests={formData.hiddenTests}
          onAdd={() => addTest('hiddenTests')}
          onRemove={(i) => removeTest('hiddenTests', i)}
          onUpdate={(i, f, v) => updateTest('hiddenTests', i, f, v)}
          onJsonChange={(i, f, v) => handleJsonChange('hiddenTests', i, f, v)}
        />
      </div>

      <div className="flex gap-4 justify-end border-t border-border pt-8 mt-4">
        <button 
          type="button"
          onClick={onCancel}
          className="px-6 py-2.5 text-sm font-semibold bg-transparent border border-border rounded-lg hover:bg-accent transition-colors"
        >
          Cancel
        </button>
        <button 
          type="submit"
          disabled={isSubmitting}
          className="px-10 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Problem' : 'Create Problem'}
        </button>
      </div>
    </form>
  );
};

const TestSection: React.FC<{
  title: string;
  subtitle: string;
  type: 'examples' | 'publicTests' | 'hiddenTests';
  tests: any[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  onUpdate: (i: number, f: string, v: any) => void;
  onJsonChange: (i: number, f: 'args' | 'expected', v: string) => void;
}> = ({ title, subtitle, type, tests, onAdd, onRemove, onUpdate, onJsonChange }) => (
  <div className="flex flex-col gap-4">
    <div className="flex justify-between items-end">
      <div>
        <h4 className="text-lg font-bold">{title}</h4>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <button 
        type="button" 
        onClick={onAdd}
        className="px-3 py-1.5 text-xs font-bold bg-secondary text-secondary-foreground border border-border rounded-md hover:bg-secondary/80 transition-colors flex items-center gap-1.5"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
        Add {type === 'examples' ? 'Example' : 'Test'}
      </button>
    </div>
    
    <div className="grid grid-cols-1 gap-4">
      {tests.length === 0 ? (
        <div className="py-8 text-center bg-muted/30 border border-dashed border-border rounded-xl text-sm text-muted-foreground italic">
          No {type.replace(/([A-Z])/g, ' $1').toLowerCase()} added yet.
        </div>
      ) : (
        tests.map((test, i) => (
          <div key={i} className="group relative p-6 bg-muted/40 border border-border rounded-xl hover:border-primary/20 transition-all">
            <button 
              type="button" 
              onClick={() => onRemove(i)}
              className="absolute top-4 right-4 p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-all opacity-0 group-hover:opacity-100"
              title="Remove Test"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
            </button>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Name / Label</label>
                <input 
                  type="text" 
                  value={test.name} 
                  onChange={e => onUpdate(i, 'name', e.target.value)} 
                  className="p-2 rounded-lg border border-border bg-background text-sm focus:ring-1 focus:ring-primary/20 outline-none"
                  placeholder="e.g., Test Case 1"
                />
              </div>
              {type === 'examples' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Explanation (Optional)</label>
                  <input 
                    type="text" 
                    value={test.explanation || ''} 
                    onChange={e => onUpdate(i, 'explanation', e.target.value)} 
                    className="p-2 rounded-lg border border-border bg-background text-sm focus:ring-1 focus:ring-primary/20 outline-none"
                    placeholder="Briefly explain this example..."
                  />
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Arguments (JSON Array)</label>
                <input 
                  type="text" 
                  value={typeof test.args === 'string' ? test.args : JSON.stringify(test.args)} 
                  onChange={e => onJsonChange(i, 'args', e.target.value)} 
                  className="p-2 rounded-lg border border-border bg-background text-sm font-mono focus:ring-1 focus:ring-primary/20 outline-none"
                  placeholder='e.g., [[1, 2, 3], 5]'
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Expected Output (JSON)</label>
                <input 
                  type="text" 
                  value={typeof test.expected === 'string' && !test.expected.startsWith('[') && !test.expected.startsWith('{') ? test.expected : JSON.stringify(test.expected)} 
                  onChange={e => onJsonChange(i, 'expected', e.target.value)} 
                  className="p-2 rounded-lg border border-border bg-background text-sm font-mono focus:ring-1 focus:ring-primary/20 outline-none"
                  placeholder='e.g., 8'
                />
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);



